import { CircleMinus, LoaderCircle, Pencil, Plus } from "lucide-react";
import type {
  Product,
  ProductCategory,
  ProductUnit,
} from "@/features/product/types";
import "./ProductLedgers.css";

export type ProductLedgerProps = {
  products: Product[];
  action: "edit" | "remove" | "add";
  pendingProductId?: string;
  onAction: (product: Product) => void;
};

const CATEGORY_LABELS: Record<ProductCategory, string> = {
  LEGUMES: "Legumes",
  FRUTAS: "Frutas",
  VERDURAS: "Verduras",
  GRAOS: "Grãos",
  TEMPEROS: "Temperos",
  OVOS: "Ovos",
  OUTROS: "Diversos",
};

const UNIT_LABELS: Record<ProductUnit, string> = {
  kg: "Kg",
  cx_kg: "Cx/Kg",
  cx_un: "Cx/Un",
  bdj: "Bandeja",
  un: "Un",
  sc_kg: "Sc/Kg",
};

const ACTION_LABELS = {
  edit: "Editar",
  remove: "Remover",
  add: "Adicionar",
} as const;

function ProductActionIcon({ action }: Pick<ProductLedgerProps, "action">) {
  switch (action) {
    case "edit":
      return <Pencil size={17} strokeWidth={1.8} />;
    case "remove":
      return <CircleMinus size={18} strokeWidth={1.8} />;
    case "add":
      return <Plus size={18} strokeWidth={1.9} />;
  }
}

export function ProductLedger({
  products,
  action,
  pendingProductId,
  onAction,
}: ProductLedgerProps) {
  if (products.length === 0) {
    return null;
  }

  const actionLabel = ACTION_LABELS[action];

  return (
    <section className="product-ledger" role="table" aria-label="Produtos">
      <div className="product-ledger-head" role="row">
        <span role="columnheader">Produto</span>
        <span role="columnheader">Categoria</span>
        <span role="columnheader">Unidade</span>
        <span className="product-ledger-action-heading" role="columnheader">
          Ação
        </span>
      </div>

      <div role="rowgroup">
        {products.map((product) => {
          const categoryLabel = CATEGORY_LABELS[product.category];
          const unitLabel = UNIT_LABELS[product.default_unit];
          const isPending = pendingProductId === product.id;

          return (
            <div
              className="product-ledger-row"
              data-testid={`product-ledger-row-${product.id}`}
              key={product.id}
              role="row"
            >
              <div className="product-ledger-identity" role="cell">
                <span className="product-ledger-name">{product.name}</span>
                <span aria-hidden="true" className="product-ledger-mobile-meta">
                  {categoryLabel} · {unitLabel}
                </span>
              </div>
              <span className="product-ledger-category" role="cell">
                {categoryLabel}
              </span>
              <span className="product-ledger-unit" role="cell">
                {unitLabel}
              </span>
              <div className="product-ledger-action-cell" role="cell">
                <button
                  aria-busy={isPending ? "true" : undefined}
                  aria-label={`${actionLabel} ${product.name}`}
                  className="product-ledger-action"
                  data-action={action}
                  disabled={isPending}
                  onClick={() => onAction(product)}
                  title={`${actionLabel} ${product.name}`}
                  type="button"
                >
                  {isPending ? (
                    <LoaderCircle
                      className="product-ledger-pending-icon"
                      size={18}
                      strokeWidth={1.8}
                    />
                  ) : (
                    <ProductActionIcon action={action} />
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
